import { Divider, FormGroup, FormParagraph, List } from '@ksyos/design-system';
import { useMemo } from 'react';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { ConclusieDiagnoseOption, GradingSummary } from '@common/enums/';
import { getDisplayName } from '@common/utils/getDisplayName';
import {
    orthODiagnosticReportCustomControlOrthoptischeDiagnoseLabels,
    TherapieOptions,
} from '@common/validation/customControls/ophthalmology';
import { translateKey } from '@frontend/translate';
import type { ICustomControlDiagnosticReportReadProps } from '../interfaces/customControlDiagnosticReportReadProps';
import { DiagnosticReportReadButtons } from '../../../elements/carePathButtons/DiagnosticReportReadButtons';
import { HeaderDivider } from '../../../elements/common';
import { ProcessContainer } from '../../episodes/common';
import { ProcessItemFooter } from '../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../episodes/ProcessItemHeader';
import { CustomControlCollapseBody } from '../utils/v1/CustomControlCollapseBody';
import { Icd10SelectionsRead } from '../utils/v1/Icd10/Icd10SelectionsRead';
import { OrthOV1ProcedureOdOsExaminationRow } from './OrthOV1OdOsExaminationRow';

export function OrthOV1DiagnosticReportConclusieRead(props: ICustomControlDiagnosticReportReadProps) {
    const translatedOrthoptischeDiagnose = useMemo(() => {
        return Object.entries(props.diagnosticReport.customControlData.orthoptische_diagnose)
            .filter((diagnosis) => diagnosis[1] && diagnosis[0] !== 'refractiestoornis')
            .map((diagnosis) => {
                return (
                    <FormParagraph key={diagnosis[0]}>
                        {orthODiagnosticReportCustomControlOrthoptischeDiagnoseLabels[diagnosis[0]]}
                    </FormParagraph>
                );
            });
    }, []);

    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.diagnosticReport && props.diagnosticReport.dateSent}
                isFirstItem={props.isFirstItem}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) =>
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || props.itemState.initialIsOpen === true,
                    })
                }
                organizationName={props.diagnosticReport.performerOrganization?.name}
                practitionerName={
                    props.diagnosticReport.performerPractitioner
                        ? getDisplayName(props.diagnosticReport.performerPractitioner)
                        : undefined
                }
                process={props.process}
                status={
                    props.diagnosticReport.gradingSummary === GradingSummary.deviant
                        ? translateKey('diagnosis.deviant')
                        : undefined
                }
                title={getServiceLabel(props.process.code, 'diagnosticReport', translateKey('common.conclusion'))}
            />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                <HeaderDivider text="Diagnose" />

                {props.diagnosticReport.customControlData.heb_je_een_orthoptische_diagnose && (
                    <>
                        <FormGroup label="Orthoptische diagnose">
                            {props.diagnosticReport.customControlData.heb_je_een_orthoptische_diagnose ===
                            ConclusieDiagnoseOption.ja ? (
                                <List>{translatedOrthoptischeDiagnose}</List>
                            ) : (
                                <FormParagraph>
                                    {props.diagnosticReport.customControlData.heb_je_een_orthoptische_diagnose}
                                </FormParagraph>
                            )}
                        </FormGroup>
                        <Icd10SelectionsRead
                            diagnosis={props.diagnosticReport.diagnosis}
                            hideTitle={true}
                            hideWorkingDiagnosisLabel={
                                !props.diagnosticReport.diagnosis?.diagnosisConsiderationIcd10 &&
                                !props.diagnosticReport.diagnosis?.considerationThesaurusTermId
                            }
                            showWorkingDiagnosis={true}
                            thesaurusTermDate={props.diagnosticReport.dateSent}
                        />
                    </>
                )}

                <Divider />
                <HeaderDivider text="Therapie" />

                {props.diagnosticReport.customControlData.therapie && (
                    <FormGroup label="Therapie">
                        <FormParagraph>{props.diagnosticReport.customControlData.therapie}</FormParagraph>
                    </FormGroup>
                )}

                {[TherapieOptions.anders, TherapieOptions.occlusie].includes(
                    props.diagnosticReport.customControlData.therapie
                ) &&
                    props.diagnosticReport.customControlData.toelichting_therapie && (
                        <FormGroup label="Therapie">
                            <FormParagraph>
                                {props.diagnosticReport.customControlData.toelichting_therapie}
                            </FormParagraph>
                        </FormGroup>
                    )}

                {props.diagnosticReport.customControlData.therapie === TherapieOptions.brilrecept && (
                    <OrthOV1ProcedureOdOsExaminationRow
                        additie_od={props.diagnosticReport.customControlData.brilrecept_additie_od}
                        additie_os={props.diagnosticReport.customControlData.brilrecept_additie_os}
                        as_od={props.diagnosticReport.customControlData.brilrecept_as_od}
                        as_os={props.diagnosticReport.customControlData.brilrecept_as_os}
                        cylindrisch_od={props.diagnosticReport.customControlData.brilrecept_cylindrisch_od}
                        cylindrisch_os={props.diagnosticReport.customControlData.brilrecept_cylindrisch_os}
                        field={'brilrecept'}
                        isReadOnly={true}
                        label={'Brilrecept'}
                        prisma_degrees_od={props.diagnosticReport.customControlData.brilrecept_prisma_degrees_od}
                        prisma_degrees_os={props.diagnosticReport.customControlData.brilrecept_prisma_degrees_os}
                        prisma_od={props.diagnosticReport.customControlData.brilrecept_prisma_od}
                        prisma_os={props.diagnosticReport.customControlData.brilrecept_prisma_os}
                        sferisch_od={props.diagnosticReport.customControlData.brilrecept_sferisch_od}
                        sferisch_os={props.diagnosticReport.customControlData.brilrecept_sferisch_os}
                    />
                )}

                <Divider />
                <HeaderDivider text="Beleid" />

                {props.diagnosticReport.customControlData.geadviseerd_beleid && (
                    <FormGroup label="Beleid">
                        <FormParagraph>{props.diagnosticReport.customControlData.geadviseerd_beleid}</FormParagraph>
                    </FormGroup>
                )}

                {props.diagnosticReport.customControlData.toelichting_beleid && (
                    <FormGroup label="Toelichting">
                        <FormParagraph>{props.diagnosticReport.customControlData.toelichting_beleid}</FormParagraph>
                    </FormGroup>
                )}

                {props.diagnosticReport.customControlData.termijn && (
                    <FormGroup label="Termijn">
                        <FormParagraph>{props.diagnosticReport.customControlData.termijn}</FormParagraph>
                    </FormGroup>
                )}

                {props.diagnosticReport.customControlData.urgentie_oogarts && (
                    <FormGroup label="Urgentie">
                        <FormParagraph>{props.diagnosticReport.customControlData.urgentie_oogarts}</FormParagraph>
                    </FormGroup>
                )}

                {props.diagnosticReport.customControlData.urgentie_optometrist && (
                    <FormGroup label="Urgentie">
                        <FormParagraph>{props.diagnosticReport.customControlData.urgentie_optometrist}</FormParagraph>
                    </FormGroup>
                )}

                {props.diagnosticReport.customControlData.toelichting_urgentie && (
                    <FormGroup label="Toelichting">
                        <FormParagraph>{props.diagnosticReport.customControlData.toelichting_urgentie}</FormParagraph>
                    </FormGroup>
                )}

                {props.diagnosticReport.customControlData.overige_relevante_informatie && (
                    <FormGroup label="Overige relevante informatie">
                        <FormParagraph>
                            {props.diagnosticReport.customControlData.overige_relevante_informatie}
                        </FormParagraph>
                    </FormGroup>
                )}
                {props.processButtons ? <>{props.processButtons} </> : <DiagnosticReportReadButtons {...props} />}
            </CustomControlCollapseBody>

            {!props.itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('diagnosticReport.hideExaminationReport')}
                    labelMore={translateKey('diagnosticReport.showExaminationReport')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
        </ProcessContainer>
    );
}
